import { NavLink } from 'react-router-dom';
import { useAdminActionCollections } from '../../../context/AdminActionCollections';
import { useUserPermissions } from '../../hooks/useUserPermissions';

export const MenuList = (
  handleMailto,
  councilMemberCanViewRankingReport = false,
) => {
  const {
    enableCommitteeAccess,
    enableCOI,
    enableScoring,
    enableCouncilMembers,
    userGuide,
    committeeUserGuide,
  } = useAdminActionCollections();

  const { isAdmin, isSCChair, isSCMember } = useUserPermissions();
  const canViewCommitteeUserGuide = isAdmin || isSCChair || isSCMember;

  return [
    // Nominations
    {
      title: 'Nominations',
      iconStyle: 'bi-file-text',
      to: 'nominations',
      access: ['fellow', 'super-admin'],
    },

    // Filtering table
    {
      title: 'My References',
      iconStyle: 'bi bi-journals',
      to: 'referee',
      access: ['referee', 'super-admin'],
    },

    // Admin menu items
    {
      title: 'Nomination',
      iconStyle: 'bi bi-list-stars',
      to: 'nomination-administration',
      access: ['admin', 'super-admin'],
    },

    {
      title: 'Referee',
      iconStyle: 'bi bi-journal-medical',
      to: 'referee-administration',
      access: ['admin', 'super-admin'],
    },

    {
      title: 'Candidates',
      iconStyle: 'bi bi-star-fill',
      to: 'candidates',
      access: !enableCommitteeAccess
        ? [
            'admin',
            'super-admin',
            ...(enableCouncilMembers ? ['council-member'] : []),
            'secretary',
          ]
        : [
            'admin',
            'committee-chair',
            'committee-member',
            'super-admin',
            'secretary',
            'council-observer',
            'council-member',
          ],
    },
    {
      title: 'COI report',
      iconStyle: 'bi bi-table',
      to: 'coi-report',
      access: !enableCOI
        ? ['admin', 'super-admin']
        : [
            'admin',
            'committee-chair',
            'super-admin',
            'secretary',
            'council-observer',
            'council-member',
          ],
    },
    {
      title: 'Candidate Scores',
      iconStyle: 'bi bi-bar-chart',
      to: 'candidate-scores',
      access: !enableScoring
        ? ['admin', 'super-admin']
        : [
            'admin',
            'committee-chair',
            'super-admin',
            'secretary',
            'council-observer',
            'council-member',
          ],
    },
    {
      title: 'Scoring completion',
      iconStyle: 'bi bi-pie-chart',
      to: 'scoring-completion',
      access: !enableScoring
        ? ['admin', 'super-admin']
        : [
            'admin',
            'committee-chair',
            'super-admin',
            'secretary',
            'council-observer',
            'council-member',
          ],
    },
    {
      title: 'Ranking Report',
      iconStyle: 'bi bi-trophy',
      to: 'ranked-candidates',
      access: [
        'admin',
        'super-admin',
        'secretary',
        'committee-chair',
        ...(councilMemberCanViewRankingReport ? ['council-member'] : []),
      ],
    },
    {
      title: 'Assessor',
      iconStyle: 'bi bi-person-plus-fill',
      to: 'assessor-administration',
      access: ['admin', 'committee-chair', 'super-admin'],
    },
    {
      title: 'Elect Candidates',
      iconStyle: 'bi bi-check',
      to: 'elect-candidate',
      access: ['admin', 'super-admin'],
    },

    // Secretary
    {
      title: 'Nomination - Secretary',
      iconStyle: 'bi bi-list-ol',
      to: 'nomination-secretary',
      access: ['secretary'],
    },
    {
      title: 'Referee - Secretary',
      iconStyle: 'bi bi-journal-text',
      to: 'referee-report-secretary',
      access: ['secretary'],
    },

    // Filtering table
    {
      title: 'Administration',
      classChange: 'mm-collapse',
      iconStyle: 'bi bi-lock',
      content: [
        {
          title: 'Admin Actions',
          to: 'admin-actions',
        },
        {
          title: 'Extension Requests',
          to: 'referee-report-extension-requests',
        },
        {
          title: 'Secretary and Council',
          to: 'secretaries-and-council-members',
        },
        {
          title: 'Committees',
          to: 'committees',
        },
        {
          title: 'Council Member allocation',
          to: 'council-member-allocations',
        },
        {
          title: 'Referee Archive',
          to: 'referee-archive',
        },
        {
          title: 'Nomination History',
          to: 'nomination-history',
        },
        {
          title: 'Cron Job Monitor',
          to: 'cron-job-monitor',
          className: 'dropdown-item',
        },
      ],
      access: ['admin', 'super-admin'],
    },
    {
      title: 'Secretary Actions',
      iconStyle: 'bi bi-download',
      to: 'secretary-actions',
      access: ['secretary'],
    },

    // Support and Help
    {
      title: 'Support and Help',
      iconStyle: 'bi bi-question-circle',
      content: [
        {
          title: 'User guide',
          custom: true,
          render: () => (
            <a
              href={
                userGuide ||
                'https://www.science.org.au/files/userfiles/fellowship/documents/FNS_2.0_User_Guide.pdf'
              }
              target='_blank'
              className='dropdown-item ai-icon no-hover'
              rel='noreferrer'
            >
              <span>User Guide</span>
            </a>
          ),
        },
        ...(canViewCommitteeUserGuide
          ? [
              {
                title: 'Committee user guide',
                custom: true,
                render: () => (
                  <a
                    href={
                      committeeUserGuide ||
                      'https://www.science.org.au/files/userfiles/fellowship/documents/FNS_2.0_User_Guide.pdf'
                    }
                    target='_blank'
                    className='dropdown-item ai-icon no-hover'
                    rel='noreferrer'
                  >
                    <span>Committee user guide</span>
                  </a>
                ),
              },
            ]
          : []),
        {
          title: 'Need Help?',
          custom: true,
          render: () => (
            <NavLink
              to='#'
              role='button'
              tabIndex='0'
              aria-label='Contact us via email'
              onClick={(e) => {
                e.preventDefault(); // Prevent navigation
                handleMailto('ict@science.org.au');
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleMailto('ict@science.org.au');
                }
              }}
            >
              <span>Contact us</span>
            </NavLink>
          ),
        },
      ],
      access: [
        'admin',
        'committee-chair',
        'committee-member',
        'council-observer',
        'council-member',
        'secretary',
        'referee',
        'fellow',
        'super-admin',
      ],
    },
  ];
};
